import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { viennaAddressesApi } from "@/api";
import { ResponseError } from "@/api/api-client";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

const HttpStatusCode = { Conflict: 409 } as const;

export function useViennaAddressImport() {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const { data: status, isLoading: isStatusLoading } = useQuery({
    queryKey: [QueryKey.ViennaAddressImportStatus],
    queryFn: () => viennaAddressesApi.getViennaAddressImportStatus(),
    refetchInterval: (query) => (query.state.data?.isImporting ? 3000 : false),
  });

  const mutation = useMutation({
    // withLocalErrorHandling suppresses the global error toast, which would otherwise fire *in addition*
    // to the one below — showing the raw English ProblemDetails detail next to the translated message.
    // onError handles every failure of this call itself, so the global handler has nothing to add.
    mutationFn: () => viennaAddressesApi.importViennaAddresses(withLocalErrorHandling),
    // The endpoint only *queues* the import now — it returns long before the data is in. Reporting
    // success here would tell the Supervisor the import finished before it had started; the status
    // query is what reports progress and completion.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ViennaAddressImportStatus] });
      showSuccess(t("settings.addresses.importStarted"));
    },
    onError: (error: unknown) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ViennaAddressImportStatus] });
      // 409 means an import is already running — a distinct outcome the Supervisor needs to be told
      // about, not the generic failure message. Anything else is a real error starting the import.
      const isAlreadyRunning = error instanceof ResponseError && error.response.status === HttpStatusCode.Conflict;

      showError(t(isAlreadyRunning ? "settings.addresses.importAlreadyRunning" : "settings.addresses.importError"));
    },
  });

  return {
    triggerImport: mutation.mutate,
    isImporting: mutation.isPending || (status?.isImporting ?? false),
    // Keep the button disabled until the initial status check resolves, so we never enable it
    // before knowing whether an import is already running.
    isStatusLoading,
    lastImportedAt: status?.lastImportedAt ?? null,
  };
}
